import React from 'react';
import classNames from 'classnames';
import {observer} from 'mobx-react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import phases from '../../store/structures/phaseList';
import styles from './ProgressBar.module.scss';
import {useStore} from '../StoreProvider';

interface ProgressBarProps {
    className?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({className}) => {
    const store = useStore();
    const {phase: currentPhase, percent: currentPercent} = store.uiStore.progressStore;

    const jumpToPhase = (phase: number) => store.uiStore.progressStore.setProgressBar({percent: 0.0, phase, slide: 0});

    const phaseComponent = (name: string, index: number, progress?: number) => {
        const canJumpPhase = Infinity !== progress && progress >= 1;
        const phaseIsActive = currentPhase === 0 && index === 1 ? true : currentPhase == index;

        return (
            <Stack
                id={`phase-${name}-${phaseIsActive ? 'is-active' : 'is-inactive'}`}
                key={index}
                direction="column"
                className={classNames(styles.phaseComponent, {[styles.phaseComponent_active]: phaseIsActive})}
            >
                {progress !== null && (
                    <Stack direction="column" justifyContent="center" className={styles.progressLine} sx={{mt: `0px`}}>
                        <Box
                            style={{width: `${progress * 100}%`}}
                            data-testid={phaseIsActive ? 'isActive' : 'isInactive'}
                        />
                    </Stack>
                )}
                <Stack
                    className={classNames(styles.buttonLabel, {[styles.cursorLabel]: canJumpPhase})}
                    justifyContent="center"
                    onClick={() => {
                        if (canJumpPhase) {
                            jumpToPhase(index === 1 ? 0 : index);
                        }
                    }}
                >
                    <Box>{name}</Box>
                </Stack>
            </Stack>
        );
    };

    const progressCalc = (currIndex: number, progressIndex: number, progress: number) => {
        if (currIndex === 1 && progressIndex === 0) return 0.035;
        if (currIndex < progressIndex) return 1;
        if (currIndex === progressIndex) return progress;
        if (currIndex > progressIndex) return 0;
    };

    return (
        <Box className={className} data-testid="ProgressBar">
            <Stack direction="row" spacing={0.5}>
                {phases.map(
                    ({name, hidden}, i) =>
                        !hidden && phaseComponent(name, i, progressCalc(i, currentPhase, currentPercent + 0.00001)),
                )}
            </Stack>
        </Box>
    );
};

export default observer(ProgressBar);
